import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import FhirDateTime from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/FhirDateTime';
import { memberDetailMessages } from './messages';
import { useDataset } from '../../../../util/api';
import { ExtractedResourceProvenance } from './ExtractedResourceProvenance';

const dateTimeParser = new FhirDateTime();

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
  allergyCell: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  modalSection: {
    marginBottom: 24,
  },
  modalField: {
    marginBottom: 12,
  },
  modalLabel: {
    display: 'block',
    marginBottom: 4,
    fontWeight: 600,
  },
  modalValue: {
    display: 'block',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: 1.5,
  },
}));

const fhirService = new FhirService('AllergyIntolerance');

const getDisplayFromUnknownConcept = (concept: unknown): string => {
  if (!concept) return '—';
  if (typeof concept === 'string') return concept || '—';
  if (typeof concept !== 'object') return '—';

  const typedConcept = concept as {
    coding?: Array<{ display?: string; code?: string }>;
    text?: string;
  };

  return (
    typedConcept.coding?.find(coding => coding.display)?.display ||
    typedConcept.coding?.[0]?.code ||
    typedConcept.text ||
    '—'
  );
};

export const getAllergyDisplay = (resource: fhir.AllergyIntolerance): string =>
  getDisplayFromUnknownConcept(resource.code);

export const getClinicalStatusDisplay = (
  resource: fhir.AllergyIntolerance,
): string => getDisplayFromUnknownConcept(resource.clinicalStatus);

export const getVerificationStatusDisplay = (
  resource: fhir.AllergyIntolerance,
): string => getDisplayFromUnknownConcept(resource.verificationStatus);

export const getCriticalityDisplay = (
  resource: fhir.AllergyIntolerance,
): string => resource.criticality || '—';

export const getRecordedDateDisplay = (
  resource: fhir.AllergyIntolerance,
): string => {
  if (!resource.onsetDateTime) return '—';
  return dateTimeParser.getDisplayValue(resource.onsetDateTime) || '—';
};

type AllergiesRow = {
  id: string;
  resource: fhir.AllergyIntolerance;
  allergy: string;
  clinicalStatus: string;
  verificationStatus: string;
  criticality: string;
  recordedDate: string;
};

type AllergyViewModalProps = {
  resource: fhir.AllergyIntolerance;
  subjectId: string;
  onDismiss: () => void;
  onRejected: (resourceId: string) => void;
};

const AllergyViewModal: React.FC<AllergyViewModalProps> = ({
  resource,
  subjectId,
  onDismiss,
  onRejected,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const allergy = getAllergyDisplay(resource);
  const clinicalStatus = getClinicalStatusDisplay(resource);
  const verificationStatus = getVerificationStatusDisplay(resource);
  const criticality = getCriticalityDisplay(resource);
  const recordedDate = getRecordedDateDisplay(resource);

  return (
    <Modal
      isOpen={true}
      size={1}
      fullWidth
      onDismiss={onDismiss}
      title={allergy}
      data-testid="allergy-view-modal"
    >
      <Box padding={2} direction="column">
        <div className={classes.modalSection}>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.allergiesAllergy)}
            </span>
            <span className={classes.modalValue}>{allergy}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.allergiesClinicalStatus)}
            </span>
            <span className={classes.modalValue}>{clinicalStatus}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.allergiesVerificationStatus)}
            </span>
            <span className={classes.modalValue}>{verificationStatus}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.allergiesCriticality)}
            </span>
            <span className={classes.modalValue}>{criticality}</span>
          </div>
          <div className={classes.modalField}>
            <span className={classes.modalLabel}>
              {formatMessage(memberDetailMessages.allergiesRecordedDate)}
            </span>
            <span className={classes.modalValue}>{recordedDate}</span>
          </div>
        </div>
        <ExtractedResourceProvenance
          resource={resource}
          resourceType="AllergyIntolerance"
          subjectId={subjectId}
          onRejected={onRejected}
        />
      </Box>
    </Modal>
  );
};

export type AllergiesContentProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const AllergiesContent: React.FC<AllergiesContentProps> = ({
  memberId,
  fallback,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const dataset = useDataset();
  const [fetchAllergies, fetchState] = useService(fhirService.get);
  const [allergies, setAllergies] = useState<fhir.AllergyIntolerance[]>([]);
  const [selectedAllergy, setSelectedAllergy] =
    useState<fhir.AllergyIntolerance | null>(null);

  const loadAllergies = useCallback(async () => {
    if (!accountId || !memberId || !auth) return;
    try {
      const responseWithParams = await fetchAllergies(accountId, {
        patient: memberId,
        _tag: [dataset.codingToken],
        _count: 200,
      });
      const entry = responseWithParams?.data?.entry ?? [];
      const resources = entry
        .map((e: { resource?: fhir.AllergyIntolerance }) => e.resource)
        .filter(Boolean) as fhir.AllergyIntolerance[];
      const sortedResources = resources.sort((a, b) => {
        const dateA = getResourceRecordedDateTime(a);
        const dateB = getResourceRecordedDateTime(b);
        return dateB - dateA;
      });
      setAllergies(sortedResources);
    } catch {
      setAllergies([]);
    }
  }, [accountId, memberId, auth, fetchAllergies, dataset.codingToken]);

  useEffect(() => {
    void loadAllergies();
  }, [loadAllergies]);

  const rows: AllergiesRow[] = allergies.map(resource => ({
    id: resource.id ?? '',
    resource,
    allergy: getAllergyDisplay(resource),
    clinicalStatus: getClinicalStatusDisplay(resource),
    verificationStatus: getVerificationStatusDisplay(resource),
    criticality: getCriticalityDisplay(resource),
    recordedDate: getRecordedDateDisplay(resource),
  }));

  const columns = [
    {
      header: {
        label: formatMessage(memberDetailMessages.allergiesAllergy),
      },
      cell: {
        content: (row: AllergiesRow) => row.allergy,
        className: classes.allergyCell,
      },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.allergiesClinicalStatus),
      },
      cell: { content: (row: AllergiesRow) => row.clinicalStatus },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.allergiesVerificationStatus),
      },
      cell: { content: (row: AllergiesRow) => row.verificationStatus },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.allergiesCriticality),
      },
      cell: { content: (row: AllergiesRow) => row.criticality },
    },
    {
      header: {
        label: formatMessage(memberDetailMessages.allergiesRecordedDate),
      },
      cell: { content: (row: AllergiesRow) => row.recordedDate },
    },
  ];

  const handleRowClick = (row: AllergiesRow) => {
    setSelectedAllergy(row.resource);
  };

  if (fetchState.isFetching && allergies.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.tabPanel}>
      <TableModule
        isLoading={fetchState.isFetching}
        config={columns}
        data={rows}
        noResultsMessage={formatMessage(memberDetailMessages.allergiesEmpty)}
        onRowClick={handleRowClick}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        rowRole="link"
      />
      {selectedAllergy && (
        <AllergyViewModal
          resource={selectedAllergy}
          subjectId={memberId}
          onDismiss={() => setSelectedAllergy(null)}
          onRejected={resourceId => {
            setAllergies(current =>
              current.filter(resource => resource.id !== resourceId),
            );
            setSelectedAllergy(null);
          }}
        />
      )}
    </div>
  );
};

export function getResourceRecordedDateTime(
  resource: fhir.AllergyIntolerance,
): number {
  if (resource.onsetDateTime) {
    return new Date(resource.onsetDateTime).getTime();
  }
  return 0;
}
